var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { isPlatformBrowser } from "@angular/common";
import { PLATFORM_ID } from "@angular/core";
import { Auth } from "@topyourthree/auth";
import { isEqual } from "@topyourthree/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import djv from "djv";
import { asyncScheduler, BehaviorSubject, firstValueFrom, fromEvent, of, Subscription, } from "rxjs";
import { delay, distinctUntilChanged, map, shareReplay, switchMap, } from "rxjs/operators";
import { definitions, topLevelSchemas } from "./json-schemas";
import { LOCAL_STORAGE_TEST_SCHEDULER } from "./local-storage.config";
import * as i0 from "@angular/core";
import * as i1 from "@topyourthree/auth";
let LocalStorage = class LocalStorage {
    constructor(auth, platformId, scheduler) {
        this.auth = auth;
        this.platformId = platformId;
        this.scheduler = scheduler;
        this.env = djv();
        this.LOGGED_OUT_KEY = "logged-out";
        this.isInitialized = false;
        this.subscriptions = new Subscription();
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    /*
     * Initialisation only occurs when the functions are being called,
     * allowing for no side effects during injection
     * http://misko.hevery.com/code-reviewers-guide/flaw-constructor-does-real-work/
     */
    init() {
        this.subjects = {
            abTestVariations: {},
            aiPromptEnabled: {},
            aiPromptSelection: {},
            aiConsultantChatHistory: {},
            aiConsultantGeneratedProjectDraft: {},
            aiPlaygroundConversationStorage: {},
            aiPlaygroundSavedTools: {},
            buildYourOwnAgentProjectDraft: {},
            appInstallPromptDismissals: {},
            appInstallPromptEmailDismissalDays: {},
            autoCloseChatEnabled: {},
            complianceQueueApproveDenyFormStates: {},
            currentTimeTrackingSession: {},
            dashboardMyProjectsUserType: {},
            developersPreference: {},
            deloitteDemo: {},
            deviceId: {},
            dontAskAgainLastChecked: {},
            doNotAskContactsSyncAgain: {},
            enterpriseContactFormSubmitted: {},
            externalQuotation: {},
            flCardsExpandedStatus: {},
            topyourthreeOnboardingResumeUploaded: {},
            topyourthreeOnboardingParsedResume: {},
            topyourthreeOnboardingResumeParsingStatus: {},
            giveGetFooterClosed: {},
            giveGetReferrerUsername: {},
            groupsHomepageSelectedView: {},
            hideMobileNewMessageToast: {},
            messagingDensityPreference: {},
            hireMeDraft: {},
            inviteToBidDiscardedTopYourThrees: {},
            nativeRatingPrompt: {},
            lastSeenPjpAssistant: {},
            lastUsedForms: {},
            loadIdPendingAction: {},
            manageRecentTable: {},
            navSavedAlertsLastReadTime: {},
            navUpdatesLastReadTime: {},
            navNotificationUpdatesLastReadTime: {},
            notificationsInternalGranted: {},
            notificationsRegistered: {},
            notificationsRequested: {},
            postJobPageDraft: {},
            postProjectObject: {},
            projectOverlayFullFlowComplete: {},
            projectOverlayThreadComplete: {},
            searchServiceFilters: {},
            showNewAccountFlow: {},
            taskList: {},
            taskListCurrentTaskClean: {},
            testName: {},
            testProfile: {},
            testProperty: {},
            theme: {},
            sectionHeroSetting: {},
            threeDSChallengeCookie: {},
            trackingConsent: {},
            userOriginalTheme: {},
            viewedItems: {},
            videochatCalling: {},
            voipNotificationsRegistered: {},
            waitingScreenSteps: {},
            webappChatDraftMessages: {},
            webappChats: {},
            webappThreadListMinimise: {},
        };
        this.authId$ = this.auth.authState$.pipe(map((authState) => authState ? `id-${authState.userId}` : this.LOGGED_OUT_KEY), shareReplay({ bufferSize: 1, refCount: true }));
        this.env.addSchema("defs.json", definitions);
        this.subscriptions.add(fromEvent(window, "storage").subscribe((e) => {
            const event = e;
            this.handleStorageEvent(event);
        }));
        this.isInitialized = true;
    }
    async handleStorageEvent(e) {
        if (!e.key || !this.isValidLocalStorageType(e.key)) {
            return;
        }
        const authId = (await firstValueFrom(this.authId$.pipe(untilDestroyed(this))));
        const subject = this.subjects[e.key];
        if (subject && subject[authId]) {
            let newObject;
            try {
                newObject = JSON.parse(e.newValue || "{}");
            }
            catch (err) {
                return;
            }
            subject[authId].next(
            // there's no way to know what type `subject` expects so we have to use `any` here
            newObject[authId]?.item);
        }
    }
    /**
     * Returns an OBSERVABLE of the current item under `key` for the current authId
     * or under the `loggedOutKey` if `loggedOutMode` is true.
     */
    get(key, loggedOutMode) {
        return of(undefined).pipe(switchMap(() => {
            if (!isPlatformBrowser(this.platformId)) {
                throw new Error("LocalStorage::get() should not be called on the server: make sure your application logic is correct");
            }
            if (!this.isInitialized) {
                this.init();
            }
            return this.authId$;
        }), 
        // TODO: T267853 - Check if this still needed when LocalStorage has been migrated
        // to use @ionic/storage-angular. We want LocalStorage::get() to be consistently
        // async, no matter the storage backend, in order to avoid storage
        // backend-specific issues.
        delay(0, this.scheduler ?? asyncScheduler), switchMap((authId) => {
            const authKey = loggedOutMode ? this.LOGGED_OUT_KEY : authId;
            const entryObj = getEntryObject(key);
            if (!entryObj) {
                // parsing error occured
                return of(undefined);
            }
            // item obtained does not conform to allowed LocalStorageTypes
            if (entryObj[authId] && !this.isValidItem(key, entryObj[authId].item)) {
                return of(undefined);
            }
            // typescript can't tell that `key` indexes into `LocalStorageTypes[T]` for some reason,
            // so we have to cast with `any` because it would otherwise treat it as a union of all possible subjectMaps
            const subjectMap = this.subjects[key];
            if (!subjectMap[authKey]) {
                subjectMap[authKey] = new BehaviorSubject(entryObj[authKey] ? entryObj[authKey].item : undefined);
            }
            /**
             * The calls to `subject.next` come from either:
             * - Someone calling `setItem`
             * - A window 'storage' event with a JSON.parse
             *
             * These can't be a pointer check so let's do a deep check.
             */
            return subjectMap[authKey].pipe(distinctUntilChanged(isEqual));
        }));
    }
    /**
     * Stores `item` in local storage for `key` under the current authId
     * or under the `loggedOutKey` if `loggedOutMode` is true.
     * If there is a subject for that particular entry, notify the observers (of that subject) of the change.
     */
    async set(key, item, loggedOutMode) {
        if (!isPlatformBrowser(this.platformId)) {
            throw new Error("LocalStorage::set() should not be called on the server: make sure your application logic is correct");
        }
        if (!this.isInitialized) {
            this.init();
        }
        const authId = await firstValueFrom(this.authId$.pipe(untilDestroyed(this)));
        // We want set() to be async as updating a local storage key when rendering
        // a component is a legitimate use case (e.g. mark as read), and
        // get()/set() being synchronous would trigger
        // "ExpressionChangedAfterItHasBeenChecked" errors
        // Promise.resolve().then() is equivalent to setTimeout()
        // TODO: T267853 - check if this still needed when LocalStorage has been migrated
        // to use @ionic/storage-angular
        return Promise.resolve().then(() => {
            const authKey = (loggedOutMode ? this.LOGGED_OUT_KEY : authId);
            setRaw(key, authKey, item);
            this.subjects[key][authKey]?.next(item);
        });
    }
    /**
     * Checks whether the item is valid against the JSON Schema.
     */
    isValidItem(key, item) {
        this.env.addSchema(key, topLevelSchemas[key]);
        if (this.env.validate(key, item)) {
            console.warn(`Failed to load ${key} from local storage as its format has changed. Check that it still validates against the schema at 'local-storage/json-schemas.ts'`);
            return false;
        }
        return true;
    }
    isValidLocalStorageType(key) {
        return key in this.subjects;
    }
};
LocalStorage.ɵfac = function LocalStorage_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LocalStorage)(i0.ɵɵinject(i1.Auth), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(LOCAL_STORAGE_TEST_SCHEDULER, 8)); };
LocalStorage.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LocalStorage, factory: LocalStorage.ɵfac, providedIn: "root" });
LocalStorage = __decorate([
    UntilDestroy({ className: "LocalStorage" }),
    __metadata("design:paramtypes", [Auth,
        Object, Object])
], LocalStorage);
export { LocalStorage };
/* HELPER FUNCTIONS */
/**
 * Directly store a @param item for a given @param authId.
 */
function setRaw(key, authId, item) {
    const entryObj = getEntryObject(key);
    if (!entryObj) {
        // parsing error occurred.
        return;
    }
    const currentTime = Date.now();
    const addItem = entryObj[authId]
        ? {
            ...entryObj[authId],
            timeUpdated: currentTime,
            item,
        }
        : {
            timeUpdated: currentTime,
            timeCreated: currentTime,
            item,
        };
    const newEntryObj = {
        ...entryObj,
        [authId]: addItem,
    };
    try {
        // if doesn't exist, creates, otherwise overrides.
        window.localStorage.setItem(key, JSON.stringify(newEntryObj));
    }
    catch (e) {
        // Do nothing
    }
}
/**
 * Fetches an object from local storage. Returns `null` if there's an error.
 */
function getEntryObject(key) {
    let entryObj;
    try {
        entryObj = JSON.parse(window.localStorage.getItem(key) || "{}");
    }
    catch (e) {
        return null;
    }
    return entryObj;
}
